





import { defineComponent } from '@nuxtjs/composition-api'

import ApplicationsKey from './ApplicationsKey.vue'
import CrossReactivityKey from './CrossReactivityKey.vue'
import LimitedUses from './LimitedUses.vue'
import SpeciesReactivity from './SpeciesReactivity.vue'
import TrademarksAndPatents from './TrademarksAndPatents.vue'

import { MappedProduct } from '@/types/product/productTypes'
import { PRODUCT_TYPES } from '@/common/utils/product/constants'
import { DATASHEET_COMPONENTS, doNotShowSupportingData } from '@/common/constants/datasheet'
import { getReactantsForSupportingData } from '@/common/utils/product/product'
import { useProductStore } from '@/store/product'

export default defineComponent({
  components: {
    ApplicationsKey,
    CrossReactivityKey,
    LimitedUses,
    SpeciesReactivity,
    TrademarksAndPatents,
  },
  props: {
    product: {
      type: Object as () => MappedProduct,
      required: true,
    },
  },
  setup({ product }) {
    const { getStatements } = useProductStore()

    let additionalInformationComponents = [
      DATASHEET_COMPONENTS.SPECIES_REACTIVITY,
      DATASHEET_COMPONENTS.APPLICATIONS_KEY,
      DATASHEET_COMPONENTS.CROSS_REACTIVITY_KEY,
      DATASHEET_COMPONENTS.TRADEMARKS_AND_PATENTS,
      DATASHEET_COMPONENTS.LIMITED_USES,
    ]

    const doNotShowSpeciesReactivity = [
      PRODUCT_TYPES.ANTIBODY_DUET,
      PRODUCT_TYPES.ANTIBODY_SAMPLER_KIT,
      PRODUCT_TYPES.CHEMICAL_MODULATORS,
      PRODUCT_TYPES.ELISA_ANTIBODY_PAIR,
      PRODUCT_TYPES.ELISA_KIT,
      PRODUCT_TYPES.FAST_SCAN_ELISA_KIT,
      PRODUCT_TYPES.GROWTH_FACTORS_AND_CYTOKINES,
      PRODUCT_TYPES.MATCHED_ANTIBODY_PAIR,
      PRODUCT_TYPES.PROTEIN_CONTROL,
      PRODUCT_TYPES.CELLULAR_DYES,
      PRODUCT_TYPES.CELL_EXTRACTS,
      PRODUCT_TYPES.CELL_EXTRACT_KIT,
      PRODUCT_TYPES.MW_MARKER,
      PRODUCT_TYPES.PTM_SCAN,
      PRODUCT_TYPES.DETECTION_SYSTEM,
      PRODUCT_TYPES.PROTEIN_CONTROL_KIT,
      PRODUCT_TYPES.DETECTION_SYSTEM_KIT,
      PRODUCT_TYPES.MW_MARKER_KIT,
    ] as const

    const createComponentObject = (component: (typeof additionalInformationComponents)[number]) => {
      switch (component) {
        case DATASHEET_COMPONENTS.SPECIES_REACTIVITY: {
          return {
            name: DATASHEET_COMPONENTS.SPECIES_REACTIVITY,
            props: { dilutionBuffer: product.attributes.dilutionBuffer },
          }
        }
        case DATASHEET_COMPONENTS.APPLICATIONS_KEY: {
          return {
            name: DATASHEET_COMPONENTS.APPLICATIONS_KEY,
            props: {
              applicationsList: product.attributes.lots?.lot.applications?.application.map((x) => {
                return { code: x.code, method: x.method }
              }),
            },
          }
        }
        case DATASHEET_COMPONENTS.CROSS_REACTIVITY_KEY: {
          return {
            name: DATASHEET_COMPONENTS.CROSS_REACTIVITY_KEY,
            props: { reactantsList: getReactantsForSupportingData(product) },
          }
        }
        case DATASHEET_COMPONENTS.TRADEMARKS_AND_PATENTS: {
          return {
            name: DATASHEET_COMPONENTS.TRADEMARKS_AND_PATENTS,
            props: { trademarks: getStatements(product.key, 'trademark'), legals: getStatements(product.key, 'legal') },
          }
        }
        case DATASHEET_COMPONENTS.LIMITED_USES: {
          return {
            name: DATASHEET_COMPONENTS.LIMITED_USES,
            props: {},
          }
        }
      }
    }

    const getComponents = () => {
      if (doNotShowSpeciesReactivity.includes(product.productType)) {
        additionalInformationComponents = additionalInformationComponents.filter(
          (x) => x !== DATASHEET_COMPONENTS.SPECIES_REACTIVITY
        )
      }

      if (doNotShowSupportingData.includes(product.productType)) {
        additionalInformationComponents = additionalInformationComponents.filter(
          (x) => x !== DATASHEET_COMPONENTS.CROSS_REACTIVITY_KEY
        )
      }

      if (doNotShowSupportingData.includes(product.productType)) {
        additionalInformationComponents = additionalInformationComponents.filter(
          (x) => x !== DATASHEET_COMPONENTS.APPLICATIONS_KEY
        )
      }
      return additionalInformationComponents.map((val) => createComponentObject(val))
    }

    const components = getComponents()

    return {
      components,
    }
  },
})
